import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import GroupTable from "@/components/tables/GroupTable";
import TableHead from "@/components/tables/TableHead";
import { fetchGroups } from "@/services/group.service";
import { BUTTONS } from "@/utils/constants/appConstants/commonConstants";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import { ALL_GROUPS } from "@/utils/constants/constants";
import { storeInLocalStorage } from "@/utils/functions/helperFunctions";
import { useDataQueries } from "@/utils/hooks/useDataQueries";
import { useWindowInfiniteScroll } from "@/utils/hooks/useWindowInfiniteScroll";
import { getListCacheBootstrap, isListFullyFetched, writeListCache } from "@/utils/listPageCache";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";

const GroupList = () => {
    const isCreateGroupPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalCreateGroup);
    const [pageNo, setPageNo, dataLength, setDataLength, sorting, setSorting] = useDataQueries();
    const listBootstrap = getListCacheBootstrap("groups", { dataLength, sorting });

    const [groups, setGroups] = useState<any>(listBootstrap.items);
    const [isLoading, setIsLoading] = useState(!listBootstrap.hasCache);
    const [dataLoad, setDataLoad] = useState(!listBootstrap.hasCache);
    const [allFetched, setAllFetched] = useState(false);
    const [groupsLeft, setGroupsLeft] = useState(true);
    const [totalCount, setTotalCount] = useState(listBootstrap.totalCount);
    const loadMoreLockRef = useRef(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        const { cacheKey, cached } = getListCacheBootstrap("groups", { dataLength, sorting });

        setPageNo(1);
        setAllFetched(false);
        setGroupsLeft(true);
        loadMoreLockRef.current = false;
        if (cached) {
            setGroups(cached.items);
            setTotalCount(cached.totalCount);
            setDataLoad(false);
            setIsLoading(false);
            if (isListFullyFetched(cached.items.length, cached.totalCount, dataLength as number)) {
                setAllFetched(true);
                setGroupsLeft(false);
            }
        } else {
            setIsLoading(true);
            setDataLoad(true);
        }

        fetchGroups(
            1,
            dataLength,
            sorting,
            (response: any) => {
                const nextGroups = response.data.groups ?? [];
                const reportedTotal = response.data.total_count;
                writeListCache(cacheKey, nextGroups, reportedTotal);
                setIsLoading(false);
                setDataLoad(false);
                setTotalCount(reportedTotal);
                loadMoreLockRef.current = false;
                if (nextGroups.length > 0) {
                    setGroups(nextGroups);
                    storeInLocalStorage(ALL_GROUPS, nextGroups);
                    const hasMore =
                        reportedTotal != null
                            ? nextGroups.length < reportedTotal
                            : nextGroups.length >= dataLength;
                    setGroupsLeft(hasMore);
                    setAllFetched(!hasMore);
                } else {
                    setAllFetched(true);
                    setGroupsLeft(false);
                }
            },
            (error: any) => {
                console.log(error);
                setIsLoading(false);
                setDataLoad(false);
                loadMoreLockRef.current = false;
            }
        );
    }, [dataLength, sorting]);

    useWindowInfiniteScroll({
        enabled: !allFetched && !isLoading && groupsLeft,
        onLoadMore: () => {
            if (loadMoreLockRef.current) {
                return;
            }
            loadMoreLockRef.current = true;

            const nextPage = Number(pageNo) + 1;
            setPageNo(nextPage);
            setIsLoading(true);

            fetchGroups(
                nextPage,
                dataLength,
                sorting,
                (response: any) => {
                    const nextGroups = response.data.groups ?? [];
                    const reportedTotal = response.data.total_count;

                    setIsLoading(false);
                    setDataLoad(false);
                    loadMoreLockRef.current = false;
                    setTotalCount(reportedTotal);

                    if (nextGroups.length > 0) {
                        setGroups((prev: any[]) => {
                            const merged = [...prev, ...nextGroups];
                            storeInLocalStorage(ALL_GROUPS, merged);
                            const hasMore =
                                reportedTotal != null
                                    ? merged.length < reportedTotal
                                    : nextGroups.length >= dataLength;
                            setGroupsLeft(hasMore);
                            setAllFetched(!hasMore);
                            return merged;
                        });
                    } else {
                        setAllFetched(true);
                        setGroupsLeft(false);
                    }
                },
                (error: any) => {
                    console.log(error);
                    setIsLoading(false);
                    loadMoreLockRef.current = false;
                }
            );
        },
    });

    return (
        <div className="gs-list-page-card">
            <div className="gs-list-page-card-header">
                <TableHead
                    id='group-table'
                    pageNo={pageNo as number}
                    dataLength={dataLength as number}
                    setDataLength={setDataLength as (dataLength: number) => void}
                    title="Groups"
                    button={
                        isCreateGroupPermitted && (
                            <Button asChild>
                                <Link to="/group" id='create-group-btn'>
                                    {BUTTONS.CREATE}
                                </Link>
                            </Button>
                        )
                    }
                    currentEnvo={groups.length}
                    totalEnvo={totalCount}
                />
            </div>
            <div>
                <GroupTable id='group-table' allFetched={allFetched} setGroups={setGroups} isLoading={isLoading} dataLength={dataLength as number} sorting={sorting} setSorting={setSorting as (sorting: any) => void} pageNo={pageNo as number} setPageNo={setPageNo as (pageNo: number) => void} groups={groups} dataLoad={dataLoad} />
            </div>
        </div>
    );
};

export default GroupList;
